import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Card, Table, Button, Modal, Form, Row, Col, Pagination } from 'react-bootstrap';
import { fetchIncomes, createIncome, updateIncome, deleteIncome } from '../../redux/slices/incomesSlice';
import { format } from 'date-fns';

const IncomeList = () => {
  const dispatch = useDispatch();
  const { incomes, pagination, loading } = useSelector((state) => state.incomes);
  const { startDate, endDate } = useSelector((state) => state.period);

  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    date: format(new Date(), 'yyyy-MM-dd'),
  });

  useEffect(() => {
    setCurrentPage(1);
    dispatch(fetchIncomes({ page: 1, startDate: startDate || null, endDate: endDate || null }));
  }, [dispatch, startDate, endDate]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    dispatch(fetchIncomes({ page, startDate: startDate || null, endDate: endDate || null }));
  };

  const handleOpenModal = (income = null) => {
    if (income) {
      setEditingIncome(income);
      setFormData({ description: income.description, amount: income.amount, date: income.date });
    } else {
      setEditingIncome(null);
      setFormData({ description: '', amount: '', date: format(new Date(), 'yyyy-MM-dd') });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingIncome(null);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const incomeData = { ...formData, amount: parseFloat(formData.amount) };

    if (editingIncome) {
      await dispatch(updateIncome({ id: editingIncome.id, data: incomeData }));
      dispatch(fetchIncomes({ page: currentPage, startDate: startDate || null, endDate: endDate || null }));
    } else {
      await dispatch(createIncome(incomeData));
      handlePageChange(1);
    }
    handleCloseModal();
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este ingreso?')) {
      await dispatch(deleteIncome(id));
      const targetPage = incomes.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      handlePageChange(targetPage);
    }
  };

  const renderPagination = () => {
    if (pagination.pages <= 1) return null;
    const items = [];
    for (let i = 1; i <= pagination.pages; i++) {
      items.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
          {i}
        </Pagination.Item>
      );
    }
    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <small className="text-muted">
          Mostrando {incomes.length} de {pagination.total} registros
        </small>
        <Pagination className="mb-0">
          <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
          {items}
          <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === pagination.pages} />
        </Pagination>
      </div>
    );
  };

  if (loading) {
    return <Container className="mt-4"><div className="text-center">Cargando...</div></Container>;
  }

  return (
    <Container className="mt-4">
      <Row className="mb-4">
        <Col><h2>Ingresos</h2></Col>
        <Col className="text-end">
          <Button variant="success" onClick={() => handleOpenModal()}>Nuevo Ingreso</Button>
        </Col>
      </Row>

      <Card className="mb-3">
        <Card.Body>
          <h4>
            Total de Ingresos:{' '}
            <span className="text-success">
              ${incomes.reduce((sum, i) => sum + i.amount, 0).toFixed(2)}
            </span>
            {pagination.total > pagination.per_page && (
              <small className="text-muted fs-6 ms-2">(página actual)</small>
            )}
          </h4>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          {incomes.length === 0 ? (
            <div className="text-center">
              <p>No tienes ingresos registrados</p>
              <Button variant="success" onClick={() => handleOpenModal()}>Agregar tu primer ingreso</Button>
            </div>
          ) : (
            <>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Descripción</th>
                    <th>Monto</th>
                    <th>Fecha</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {incomes.map((income) => (
                    <tr key={income.id}>
                      <td>{income.description}</td>
                      <td className="text-success">${income.amount.toFixed(2)}</td>
                      <td>{format(new Date(income.date + 'T00:00:00'), 'dd/MM/yyyy')}</td>
                      <td>
                        <Button variant="warning" size="sm" className="me-2" onClick={() => handleOpenModal(income)}>
                          Editar
                        </Button>
                        <Button variant="danger" size="sm" onClick={() => handleDelete(income.id)}>
                          Eliminar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {renderPagination()}
            </>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{editingIncome ? 'Editar Ingreso' : 'Nuevo Ingreso'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                type="text" name="description" value={formData.description}
                onChange={handleChange} placeholder="Ej: Salario, Freelance, etc." required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Monto</Form.Label>
              <Form.Control
                type="number" step="0.01" name="amount" value={formData.amount}
                onChange={handleChange} placeholder="0.00" required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Fecha</Form.Label>
              <Form.Control type="date" name="date" value={formData.date} onChange={handleChange} required />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>Cancelar</Button>
            <Button variant="success" type="submit">{editingIncome ? 'Actualizar' : 'Crear'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default IncomeList;
